import { apiRequest } from '../../shared/api/client';
import { ApiError } from '../../shared/api/problem';

/**
 * ABC classification — mirrors Invetrak.Api.Endpoints.AbcClassificationEndpoints (camelCase).
 *
 *   GET  /api/v1/warehouses/{warehouseId}/abc-classification
 *   POST /api/v1/warehouses/{warehouseId}/abc-classification/recalculate
 *
 * Reads need items:read, recalculating needs items:write (enforced server-side).
 *
 * ABC is calculated, never entered: cost multiplied by movement over a per-warehouse window,
 * ranked highest-first, and cut into three bands that are shares of total movement VALUE — not
 * of item counts. It is the input cycle counting is built on, and it is optional: plenty of
 * warehouses will never turn it on.
 */

export type AbcOutcome = 'classified' | 'no_items' | 'no_movement' | 'no_costs';

export interface AbcDistributionDto {
  warehouseId: string;
  /** Null on every outcome except `classified` — a refusal writes no snapshot. */
  snapshotDateUtc: string | null;
  movementPeriodDays: number;
  outcome: AbcOutcome;
  itemsConsidered: number;
  itemsClassified: number;
  classACount: number;
  classBCount: number;
  classCCount: number;
  totalDollarMovement: number;
  classADollars: number;
  classBDollars: number;
  classCDollars: number;
  /**
   * Items that moved but carry no usable cost, so they scored zero and ranked at the bottom.
   * Null when reading a stored snapshot rather than running: it is not recorded, and
   * recomputing it would measure today's catalogue against an older run.
   */
  itemsWithMovementButNoCost: number | null;
  currencyCode: string;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

function base(warehouseId: string): string {
  return `/v1/warehouses/${encodeURIComponent(warehouseId)}/abc-classification`;
}

/**
 * The latest snapshot, or null if this warehouse has never been classified.
 *
 * 404 is translated to null rather than thrown: never having run ABC is the normal state for
 * any warehouse that does not use cycle counting, so it is an answer, not a failure.
 */
export async function getAbcDistribution(
  warehouseId: string,
  signal?: AbortSignal,
): Promise<AbcDistributionDto | null> {
  try {
    const { data } = await apiRequest<AbcDistributionDto>(`${base(warehouseId)}/`, {
      method: 'GET',
      signal,
      ...COOKIE_INIT,
    });
    return data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Recalculates and snapshots. Re-running on the same day replaces that day's snapshot.
 *
 * A 200 does not mean a snapshot was written — check `outcome`. The three refusals
 * (`no_items`, `no_movement`, `no_costs`) are successful requests that found nothing rankable
 * and deliberately stored nothing, because filing an entire warehouse under class C would look
 * like a real result.
 */
export async function recalculateAbc(
  warehouseId: string,
  signal?: AbortSignal,
): Promise<AbcDistributionDto> {
  const { data } = await apiRequest<AbcDistributionDto>(`${base(warehouseId)}/recalculate`, {
    method: 'POST',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** Share of total movement value held by one class, as a percentage. Zero total ⇒ zero share. */
export function valueShare(dollars: number, total: number): number {
  if (total <= 0) return 0;
  return (dollars / total) * 100;
}
